import { Action } from '@ngrx/store';
import { PageableResults, Paging } from 'src/app/api/types/Pageable';
import {
  BulkOrderUpdateRequest,
  ClientPaymentStatus,
  ClientStoreOrder,
  ClientStoreOrderStatus,
} from '../store-order';
import { Order } from '../../stores';

import { TabSortFilterParams } from '../../+state/stores.reducer';
import { HideLoader } from 'src/app/shared/decorators';
import { Event } from '../../event';

export enum StoreOrderActionType {
  Initialize = '[storeOrder] Initialize',
  LoadStoreOrdersSortFilter = '[storeOrder] LoadStoreOrdersSortFilter',
  LoadStoreOrdersSortFilterSuccess = '[storeOrder] LoadStoreOrdersSortFilterSuccess',
  LoadStoreOrdersSortFilterFail = '[storeOrder] LoadStoreOrdersSortFilterFail',
  UpdateOrderStatus = '[storeOrder] UpdateOrderStatus',
  UpdateOrderStatusSuccess = '[storeOrder] UpdateOrderStatusSuccess',
  UpdateOrderStatusFail = '[storeOrder] UpdateOrderStatusFail',
  UpdatePaymentStatus = '[storeOrder] UpdatePaymentStatus',
  UpdatePaymentStatusSuccess = '[storeOrder] UpdatePaymentStatusSuccess',
  UpdatePaymentStatusFail = '[storeOrder] UpdatePaymentStatusFail',
  UpdateBulkOrderStatus = '[storeOrder] UpdateBulkOrderStatus',
  UpdateBulkOrderStatusSuccess = '[storeOrder] UpdateBulkOrderStatusSuccess',
  UpdateBulkOrderStatusFail = '[storeOrder] UpdateBulkOrderStatusFail',
  LoadStoreOrder = '[storeOrder] LoadStoreOrder',
  LoadStoreOrderSuccess = '[storeOrder] LoadStoreOrderSuccess',
  LoadStoreOrderFail = '[storeOrder] LoadStoreOrderFail',
  DownloadOrderPdf = '[storeOrder] DownloadOrderPdf',
  DownloadOrderPdfSuccess = '[storeOrder] DownloadOrderPdfSuccess',
  DownloadOrderPdfFailed = '[storeOrder] DownloadOrderPdfFailed',
  DownloadAndPrintOrderPdf = '[storeOrder] DownloadAndPrintOrderPdf',
  DownloadAndPrintOrderPdfSuccess = '[storeOrder] DownloadAndPrintOrderPdfSuccess',
  DownloadAndPrintOrderPdfFailed = '[storeOrder] DownloadAndPrintOrderPdfFailed',
  AddSelectedOrder = '[storeOrder] AddSelectedOrder',
  RemoveSelectedOrder = '[storeOrder] RemoveSelectedOrder',
  ClearSelectedOrders = '[storeOrder] ClearSelectedOrders',
  SetSelectedOrders = '[storeOrder] SetSelectedOrders',
  // Card #9601 — Admin confirms a PENDING_CONFIRMATION order
  ConfirmPendingOrder = '[storeOrder] ConfirmPendingOrder',
  ConfirmPendingOrderSuccess = '[storeOrder] ConfirmPendingOrderSuccess',
  ConfirmPendingOrderFail = '[storeOrder] ConfirmPendingOrderFail',
  // Card #9601 — Child store marketplace bidding
  SelectInheritedOrder = '[storeOrder] SelectInheritedOrder',
  SubmitBid = '[storeOrder] SubmitBid',
  SubmitBidSuccess = '[storeOrder] SubmitBidSuccess',
  SubmitBidFail = '[storeOrder] SubmitBidFail',
  PassBid = '[storeOrder] PassBid',
  PassBidSuccess = '[storeOrder] PassBidSuccess',
  PassBidFail = '[storeOrder] PassBidFail',
}

export class Initialize implements Action {
  readonly type = StoreOrderActionType.Initialize;

  constructor() {}
}

export class LoadStoreOrdersSortFilter implements Action {
  readonly type = StoreOrderActionType.LoadStoreOrdersSortFilter;

  constructor(
    public readonly storeId: number,
    public readonly tabName: string,
    public readonly tabSortFilterParams: TabSortFilterParams,
    public readonly paging?: Paging,
    public readonly event?: Event,
  ) {}
}

export class LoadStoreOrdersSortFilterSuccess implements Action {
  readonly type = StoreOrderActionType.LoadStoreOrdersSortFilterSuccess;

  constructor(
    public readonly orders: PageableResults<ClientStoreOrder>,
    public readonly event?: Event,
  ) {}
}

export class LoadStoreOrdersSortFilterFail implements Action {
  readonly type = StoreOrderActionType.LoadStoreOrdersSortFilterFail;

  constructor(public readonly event?: Event) {}
}

export class UpdateOrderStatus implements Action {
  readonly type = StoreOrderActionType.UpdateOrderStatus;

  constructor(
    public readonly orderUuid: string,
    public readonly status: ClientStoreOrderStatus,
    public readonly rejectReason: string,
    public readonly estimatedTime: Date,
    public readonly isReady: boolean,
  ) {}
}

export class UpdateOrderStatusSuccess implements Action {
  readonly type = StoreOrderActionType.UpdateOrderStatusSuccess;

  constructor(public readonly orderUuid: string) {}
}

export class UpdateOrderStatusFail implements Action {
  readonly type = StoreOrderActionType.UpdateOrderStatusFail;

  constructor(public readonly error: string) {}
}

export class UpdatePaymentStatus implements Action {
  readonly type = StoreOrderActionType.UpdatePaymentStatus;

  constructor(
    public readonly orderUuid: string,
    public readonly paymentStatus: ClientPaymentStatus,
  ) {}
}

export class UpdatePaymentStatusSuccess implements Action {
  readonly type = StoreOrderActionType.UpdatePaymentStatusSuccess;

  constructor(public readonly orderUuid: string) {}
}

export class UpdatePaymentStatusFail implements Action {
  readonly type = StoreOrderActionType.UpdatePaymentStatusFail;

  constructor(public readonly error: string) {}
}

export class UpdateBulkOrderStatus implements Action {
  readonly type = StoreOrderActionType.UpdateBulkOrderStatus;

  constructor(public readonly bulkOrderUpdateRequest: BulkOrderUpdateRequest) {}
}

export class UpdateBulkOrderStatusSuccess implements Action {
  readonly type = StoreOrderActionType.UpdateBulkOrderStatusSuccess;

  constructor() {}
}

export class UpdateBulkOrderStatusFail implements Action {
  readonly type = StoreOrderActionType.UpdateBulkOrderStatusFail;

  constructor(public readonly error: string) {}
}
export class LoadStoreOrder implements Action {
  readonly type = StoreOrderActionType.LoadStoreOrder;

  constructor(
    public readonly storeId: number,
    public readonly orderUuid: string,
    public readonly stationId?: number,
  ) {}
}
@HideLoader(StoreOrderActionType.LoadStoreOrder)
export class LoadStoreOrderSuccess implements Action {
  readonly type = StoreOrderActionType.LoadStoreOrderSuccess;

  constructor(public readonly order: Order) {}
}
@HideLoader(StoreOrderActionType.LoadStoreOrder)
export class LoadStoreOrderFail implements Action {
  readonly type = StoreOrderActionType.LoadStoreOrderFail;

  constructor() {}
}

export class DownloadOrderPdf implements Action {
  readonly type = StoreOrderActionType.DownloadOrderPdf;

  constructor(
    public readonly storeId: number,
    public readonly orderId: string,
    public readonly uiLanguageLocale: string,
    public readonly catalogLanguageLocale: string,
    public readonly stationId?: number,
  ) {}
}

export class DownloadOrderPdfSuccess implements Action {
  readonly type = StoreOrderActionType.DownloadOrderPdfSuccess;

  constructor(
    public readonly blob: Blob,
    public readonly filename: string,
  ) {}
}

export class DownloadOrderPdfFailed implements Action {
  readonly type = StoreOrderActionType.DownloadOrderPdfFailed;

  constructor(public readonly error: string) {}
}

export class DownloadAndPrintOrderPdf implements Action {
  readonly type = StoreOrderActionType.DownloadAndPrintOrderPdf;

  constructor(
    public readonly storeId: number,
    public readonly orderId: string,
    public readonly uiLanguageLocale: string,
    public readonly catalogLanguageLocale: string,
    public readonly stationId?: number,
  ) {}
}

export class DownloadAndPrintOrderPdfSuccess implements Action {
  readonly type = StoreOrderActionType.DownloadAndPrintOrderPdfSuccess;

  constructor(
    public readonly blob: Blob,
    public readonly filename: string,
  ) {}
}

export class DownloadAndPrintOrderPdfFailed implements Action {
  readonly type = StoreOrderActionType.DownloadAndPrintOrderPdfFailed;

  constructor(public readonly error: string) {}
}

export class AddSelectedOrder implements Action {
  readonly type = StoreOrderActionType.AddSelectedOrder;

  constructor(public readonly orderUUID: string) {}
}

export class RemoveSelectedOrder implements Action {
  readonly type = StoreOrderActionType.RemoveSelectedOrder;

  constructor(public readonly orderUUID: string) {}
}

export class ClearSelectedOrders implements Action {
  readonly type = StoreOrderActionType.ClearSelectedOrders;

  constructor() {}
}

export class SetSelectedOrders implements Action {
  readonly type = StoreOrderActionType.SetSelectedOrders;

  constructor() {}
}

export class ConfirmPendingOrder implements Action {
  readonly type = StoreOrderActionType.ConfirmPendingOrder;
  constructor(
    public storeId: number,
    public orderUuid: string,
    public etaMinutes: number | null,
  ) {}
}

export class ConfirmPendingOrderSuccess implements Action {
  readonly type = StoreOrderActionType.ConfirmPendingOrderSuccess;
  constructor(public orderUuid: string) {}
}

export class ConfirmPendingOrderFail implements Action {
  readonly type = StoreOrderActionType.ConfirmPendingOrderFail;
  constructor(public error: string) {}
}

// Card #9601 — Child store marketplace bidding actions

export class SelectInheritedOrder implements Action {
  readonly type = StoreOrderActionType.SelectInheritedOrder;
  constructor(public order: Order) {}
}

export class SubmitBid implements Action {
  readonly type = StoreOrderActionType.SubmitBid;
  constructor(
    public childStoreId: number,
    public parentOrderId: number,
    public etaMinutes: number | null,
  ) {}
}

export class SubmitBidSuccess implements Action {
  readonly type = StoreOrderActionType.SubmitBidSuccess;
  constructor() {}
}

export class SubmitBidFail implements Action {
  readonly type = StoreOrderActionType.SubmitBidFail;
  constructor(public error: string) {}
}

export class PassBid implements Action {
  readonly type = StoreOrderActionType.PassBid;
  constructor(
    public childStoreId: number,
    public parentOrderId: number,
  ) {}
}

export class PassBidSuccess implements Action {
  readonly type = StoreOrderActionType.PassBidSuccess;
  constructor() {}
}

export class PassBidFail implements Action {
  readonly type = StoreOrderActionType.PassBidFail;
  constructor(public error: string) {}
}

export type StoreOrderAction =
  | Initialize
  | LoadStoreOrdersSortFilter
  | LoadStoreOrdersSortFilterSuccess
  | LoadStoreOrdersSortFilterFail
  | UpdateOrderStatus
  | UpdateOrderStatusSuccess
  | UpdateOrderStatusFail
  | UpdatePaymentStatus
  | UpdatePaymentStatusSuccess
  | UpdatePaymentStatusFail
  | UpdateBulkOrderStatus
  | UpdateBulkOrderStatusSuccess
  | UpdateBulkOrderStatusFail
  | LoadStoreOrder
  | LoadStoreOrderSuccess
  | LoadStoreOrderFail
  | DownloadOrderPdf
  | DownloadOrderPdfSuccess
  | DownloadOrderPdfFailed
  | DownloadAndPrintOrderPdf
  | DownloadAndPrintOrderPdfSuccess
  | DownloadAndPrintOrderPdfFailed
  | AddSelectedOrder
  | RemoveSelectedOrder
  | ClearSelectedOrders
  | SetSelectedOrders
  | ConfirmPendingOrder
  | ConfirmPendingOrderSuccess
  | ConfirmPendingOrderFail
  | SelectInheritedOrder
  | SubmitBid
  | SubmitBidSuccess
  | SubmitBidFail
  | PassBid
  | PassBidSuccess
  | PassBidFail;
